import type { JSX } from "react";
import {
  Card,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  StatePill,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";

import { ApiError, apiGet } from "../../../lib/api.js";
import { formatCount, formatDateTime, formatTimestampSafe } from "../../../locale-format.js";

type PurgeJobState =
  | "pending"
  | "previewed"
  | "running"
  | "succeeded"
  | "partial_failed"
  | "failed";

interface PurgeJobRow {
  readonly id: string;
  readonly tenantDisplayLabel: string;
  readonly state: PurgeJobState;
  readonly actorEmail: string;
  readonly previewedAt: string;
  readonly deletionCounts?: Readonly<Record<string, number | undefined>>;
}

interface PurgeJobsResponse {
  readonly items: readonly PurgeJobRow[];
}

const STATE_TONE: Readonly<Record<PurgeJobState, StatePillTone>> = {
  pending: "neutral",
  previewed: "info",
  running: "info",
  succeeded: "success",
  partial_failed: "warn",
  failed: "danger",
};

const PURGE_JOB_STATES = Object.keys(STATE_TONE) as readonly PurgeJobState[];

function isForbidden(error: Error): boolean {
  return error instanceof ApiError && error.status === 403;
}

function isPurgeJobState(value: string): value is PurgeJobState {
  return PURGE_JOB_STATES.includes(value as PurgeJobState);
}

/** Unknown server states clamp to neutral instead of crashing the pill. */
function toneFor(state: string): StatePillTone {
  return isPurgeJobState(state) ? STATE_TONE[state] : "neutral";
}

function isJob(value: unknown): value is PurgeJobRow {
  if (typeof value !== "object" || value === null) return false;
  const job = value as Record<string, unknown>;
  return (
    typeof job.id === "string" &&
    typeof job.tenantDisplayLabel === "string" &&
    typeof job.state === "string" &&
    typeof job.actorEmail === "string" &&
    typeof job.previewedAt === "string"
  );
}

function isJobsResponse(value: PurgeJobsResponse | undefined): value is PurgeJobsResponse {
  const body = value as unknown;
  if (typeof body !== "object" || body === null) return false;
  const items = (body as Record<string, unknown>).items;
  return Array.isArray(items) && items.every(isJob);
}

/** Sum of all deleted rows; absent or non-finite counters are ignored. */
function totalRows(counts: PurgeJobRow["deletionCounts"]): number {
  if (typeof counts !== "object" || counts === null) return 0;
  let sum = 0;
  for (const value of Object.values(counts)) {
    if (typeof value === "number" && Number.isFinite(value)) sum += value;
  }
  return sum;
}

/**
 * A-GTCC-17 — read-only purge history backed by the existing Owner GET.
 *
 * Previewing and executing a purge stay in the Purge Center: both are write
 * actions and must not be reachable from the cockpit. This view only reads
 * `GET /v1/admin/tenants/purge-jobs` so an operator can see the audit trail.
 */
export function GtccPurgeView(): JSX.Element {
  const { t } = useLocale();
  const query = useQuery<PurgeJobsResponse, Error>({
    queryKey: ["gtcc", "purge", "jobs"],
    queryFn: ({ signal }) =>
      apiGet<PurgeJobsResponse>("/v1/admin/tenants/purge-jobs", undefined, signal),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const data = isJobsResponse(query.data) ? query.data : undefined;
  const jobs = data?.items ?? [];

  return (
    <Stack gap={4} style={{ padding: "var(--gt-sys-space-5)" }} data-testid="gtcc-view-purge">
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.purge.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>{t("admin.purge.subtitle")}</p>
        <Inline gap={2} align="center" wrap>
          <StatePill tone="info" label={t("admin.gtcc.ovw.read_only_note")} />
        </Inline>
        <p
          style={{
            margin: 0,
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
          data-testid="gtcc-purge-read-only-note"
        >
          {t("admin.purge.callout.body")}
        </p>
      </Stack>

      {query.isPending ? <LoadingState label={t("common.loading")} /> : null}
      {query.isError ? (
        <ErrorState
          severity={isForbidden(query.error) ? "warn" : "error"}
          title={
            isForbidden(query.error)
              ? t("admin.purge.gate.title")
              : t("admin.purge.preview.error.generic_title")
          }
          body={
            isForbidden(query.error)
              ? t("admin.purge.gate.body")
              : t("admin.purge.preview.error.generic_body")
          }
        />
      ) : null}
      {!query.isPending && !query.isError && !data ? (
        <EmptyState title={t("admin.purge.counts.empty")} />
      ) : null}
      {data && jobs.length === 0 ? <EmptyState title={t("admin.purge.counts.empty")} /> : null}

      {data && jobs.length > 0 ? (
        <Card>
          <Stack gap={3}>
            <h3
              id="gtcc-purge-history-heading"
              style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}
            >
              {t("admin.purge.history.title")}
            </h3>
            <table
              data-testid="gtcc-purge-history-table"
              aria-labelledby="gtcc-purge-history-heading"
              style={{ width: "100%" }}
            >
              <thead>
                <tr>
                  <th scope="col">{t("admin.purge.history.col.tenant")}</th>
                  <th scope="col">{t("admin.purge.history.col.state")}</th>
                  <th scope="col">{t("admin.purge.history.col.actor")}</th>
                  <th scope="col">{t("admin.purge.history.col.previewed_at")}</th>
                  <th scope="col">{t("admin.purge.counts.col.count")}</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.tenantDisplayLabel}</td>
                    <td>
                      <StatePill tone={toneFor(job.state)} label={job.state} />
                    </td>
                    <td>{job.actorEmail}</td>
                    <td>{formatTimestampSafe(job.previewedAt, (d) => formatDateTime(d))}</td>
                    <td>{formatCount(totalRows(job.deletionCounts))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
}
